import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Building, MapPin, Phone, AlertTriangle, Eye, EyeOff, Mail, User, ArrowLeft } from "lucide-react";
import { TradesmanPortfolioUpload } from "@/components/TradesmanPortfolioUpload";
import { GooglePlacesLocation } from "@/components/GooglePlacesLocation";
import { GooglePlacesAddress } from "@/components/GooglePlacesAddress";
import { EmailVerificationDialog } from "@/components/EmailVerificationDialog";
import { ModernLogo } from "@/components/ModernLogo";

const availableJobCategories = [
  "Plumbing", "Electrical", "Carpentry", "HVAC", "Painting", "Roofing", 
  "Flooring", "Landscaping", "Cleaning", "General Maintenance", 
  "Masonry", "Locksmith", "Appliance Repair", "Pest Control",
  "Plastering", "Dry-lining", "Wallpapering", "Welding", "Scaffolding"
];

export function TradesmanRegistration() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tradesmanId, setTradesmanId] = useState<string | null>(null);
  const [canComplete, setCanComplete] = useState(false);
  const [locationValue, setLocationValue] = useState("");
  const [address, setAddress] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Removed auto-redirect - users need to complete registration flow

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else if (prev.length < 5) {
        return [...prev, category];
      } else {
        toast({
          title: "Maximum categories reached",
          description: "You can select up to 5 categories. Remove one to add another.",
          variant: "destructive",
        });
        return prev;
      }
    });
  };

  const validateUKPhone = (phone: string) => {
    const ukPhoneRegex = /^(?:(?:\+44)|(?:0))(?:(?:7[0-9]{9})|(?:1[0-9]{9})|(?:2[0-9]{9})|(?:3[0-9]{9})|(?:8[0-8][0-9]{8})|(?:9[0-9]{9}))$/;
    return ukPhoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const fullName = formData.get("fullName") as string;
    const phone = formData.get("phone") as string;

    if (!validateUKPhone(phone)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid UK phone number (e.g., 07123456789 or 01234567890).",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      const { data: authData, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName,
            user_type: 'tradesman',
            phone,
            address,
          },
        },
      });

      if (error) throw error;

      if (authData.user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", authData.user.id)
          .single();
        
        if (profile) {
          setTradesmanId(profile.id);
          setUserProfile(profile);
        }
      }

      toast({
        title: "Account created!",
        description: "Now complete your tradesman profile to start receiving jobs.",
      });
      
      setCurrentStep(2);
    } catch (error: any) {
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const companyName = formData.get("companyName") as string;

    if (selectedCategories.length === 0) {
      toast({
        title: "Please select at least one job category",
        description: "You must select at least one job category to continue.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("profiles")
        .update({
          company_name: companyName,
          location: locationValue,
          job_categories: selectedCategories,
        })
        .eq("user_id", user.user.id);

      if (error) throw error;

      setCurrentStep(3);
      toast({
        title: "Profile updated!",
        description: "Now upload your insurance certificate and portfolio photos.",
      });
    } catch (error: any) {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowEmailVerification = async () => {
    if (!emailSent) {
      // Send email verification automatically
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && userProfile) {
          const { error } = await supabase.functions.invoke('verify-contact', {
            body: {
              type: 'email',
              action: 'request',
              email: userProfile.email,
            },
          });

          if (!error) {
            setEmailSent(true);
            toast({
              title: "Verification email sent",
              description: "Check your email for the verification code.",
            });
          }
        }
      } catch (error) {
        console.error('Error sending verification email:', error);
      }
    }
    setShowEmailVerification(true);
  };

  const handleCompleteRegistration = async () => {
    if (!canComplete) {
      toast({
        title: "Documents required",
        description: "Please upload both your insurance certificate and portfolio photos to complete registration.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Refresh latest profile to check verification
        const { data: profile } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", user.id)
          .single();

        if (!profile) return;

        if (!profile.email_verified) {
          setUserProfile(profile);
          setShowEmailVerification(true);
          toast({
            title: "Email verification required",
            description: "Please verify your email to complete registration.",
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Registration complete!",
          description: "Your tradesman profile has been created successfully.",
        });

        // Navigate to success page instead of dashboard
        navigate('/tradesman-registration-success');
      }
    } catch (error) {
      console.error('Error completing registration:', error);
      toast({
        title: "Registration error",
        description: "There was an issue completing your registration. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/30 via-background to-blue-100/20 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-subtle opacity-50"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-blue opacity-20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-hero opacity-10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      <div className="w-full max-w-4xl relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-between items-center mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all duration-300 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
            
            <ModernLogo size="lg" className="transition-transform hover:scale-105" showTagline={false} />
            
            <div className="w-[120px]" /> {/* Spacer for centering */}
          </div>
          <h1 className="text-4xl font-bold text-white bg-gradient-hero px-6 py-3 rounded-full shadow-glow animate-fade-in inline-block mb-4">
            {currentStep === 1 ? "Join as a Tradesman" : 
             currentStep === 2 ? "Complete Your Profile" : 
             "Upload Documents & Portfolio"}
          </h1>
          <p className="text-lg text-blue-800 font-medium">
            {currentStep === 1 ? "Create your account to start receiving job opportunities" : 
             currentStep === 2 ? "Set up your specialties and business information" :
             "Upload your insurance certificate and showcase your work"}
          </p>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className={`h-3 w-12 rounded-full transition-all duration-500 ${currentStep >= 1 ? 'bg-gradient-hero shadow-glow' : 'bg-blue-200'}`} />
            <div className={`h-3 w-12 rounded-full transition-all duration-500 ${currentStep >= 2 ? 'bg-gradient-hero shadow-glow' : 'bg-blue-200'}`} />
            <div className={`h-3 w-12 rounded-full transition-all duration-500 ${currentStep >= 3 ? 'bg-gradient-hero shadow-glow' : 'bg-blue-200'}`} />
          </div>
        </div>

        {/* Step 1: Account Creation */}
        {currentStep === 1 && (
          <Card className="shadow-elegant hover:shadow-glow transition-all duration-300 border-0 bg-gradient-blue text-white animate-fade-in">
            <CardHeader className="bg-white/10 backdrop-blur-sm">
              <CardTitle className="text-2xl text-white flex items-center gap-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                Create Your Account
              </CardTitle>
              <CardDescription className="text-white/90 text-base">
                Sign up to start your tradesman journey with Sortable
              </CardDescription>
            </CardHeader>
            <CardContent className="bg-white/95 backdrop-blur-md p-6 sm:p-8">
              <form onSubmit={handleSignUp} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-sm font-medium text-foreground">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-600 h-4 w-4" />
                    <Input
                      id="fullName"
                      name="fullName"
                      placeholder="e.g., John Smith"
                      className="pl-10 h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-foreground">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-600 h-4 w-4" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="e.g., john@example.com"
                      className="pl-10 h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a secure password"
                      className="h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-foreground placeholder:text-muted-foreground pr-10"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-11 px-3 hover:bg-blue-50 transition-colors duration-200"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-blue-600" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-foreground">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-600 h-4 w-4" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="e.g., 07123456789 or 01234567890"
                      className="pl-10 h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-sm font-medium text-foreground">Home Address</Label>
                  <GooglePlacesAddress
                    id="address"
                    name="address"
                    value={address}
                    onChange={setAddress}
                    placeholder="Your full home address"
                    className="h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 bg-gradient-hero text-white shadow-glow hover:shadow-elegant transition-all duration-300 border-0"
                  size="lg"
                >
                  {isLoading ? "Creating account..." : "Create Account"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Profile Setup */}
        {currentStep === 2 && (
          <Card className="shadow-elegant hover:shadow-glow transition-all duration-300 border-0 bg-gradient-hero text-white animate-fade-in">
            <CardHeader className="bg-white/10 backdrop-blur-sm">
              <CardTitle className="text-2xl text-white flex items-center gap-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Building className="w-5 h-5 text-white" />
                </div>
                Professional Information
              </CardTitle>
              <CardDescription className="text-white/90 text-base">
                Tell us about your trade specialties and business details
              </CardDescription>
            </CardHeader>
            <CardContent className="bg-white/95 backdrop-blur-md p-6 sm:p-8">
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-sm font-medium text-foreground">Company Name</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-600 h-4 w-4" />
                    <Input
                      id="companyName"
                      name="companyName"
                      placeholder="e.g., Smith Plumbing Services"
                      className="pl-10 h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location" className="text-sm font-medium text-foreground">Service Area</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-600 h-4 w-4 z-10" />
                    <GooglePlacesLocation
                      id="location"
                      name="location"
                      value={locationValue}
                      onChange={setLocationValue}
                      placeholder="e.g., London, Manchester, Birmingham"
                      className="pl-10 h-11 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 border-blue-200 hover:border-blue-300 bg-white shadow-sm text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-lg font-semibold text-foreground">
                      Job Categories ({selectedCategories.length}/5)
                    </Label>
                    <span className="text-sm text-blue-600 font-medium bg-blue-100 px-3 py-1 rounded-full">
                      Choose up to 5 categories
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availableJobCategories.map((category) => {
                      const isSelected = selectedCategories.includes(category);
                      const isDisabled = !isSelected && selectedCategories.length >= 5;
                      
                      return (
                        <Button
                          key={category}
                          type="button"
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          disabled={isDisabled}
                          onClick={() => handleCategoryToggle(category)}
                          className={`h-auto p-4 justify-center transition-all duration-300 ${
                            isSelected ? "bg-gradient-hero text-white shadow-glow" : "border-blue-200 hover:border-blue-400 hover:bg-blue-50 text-foreground hover:text-foreground"
                          } ${isDisabled ? "opacity-50" : ""}`}
                        >
                          <div className="flex items-center justify-center gap-2">
                            {isSelected && <CheckCircle className="h-4 w-4" />}
                            <span className="text-sm font-medium leading-tight text-center">
                              {category}
                            </span>
                          </div>
                        </Button>
                      );
                    })}
                  </div>

                  {selectedCategories.length > 0 && (
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg border border-blue-200 shadow-sm">
                      <p className="text-sm font-semibold text-blue-900 mb-3">Selected Categories:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedCategories.map((category) => (
                          <Badge key={category} className="bg-gradient-hero text-white shadow-sm">
                            {category}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isLoading || selectedCategories.length === 0}
                  className="w-full h-12 bg-gradient-hero text-white shadow-glow hover:shadow-elegant transition-all duration-300 border-0"
                  size="lg"
                >
                  {isLoading ? "Updating profile..." : "Continue to Documents"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Document Upload */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <Card className="p-4 bg-gradient-to-r from-red-100 to-red-100 border border-red-300 shadow-elegant">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-red-800 mb-2 text-base">⚠️ Required Before Account Activation</p>
                  <ul className="text-red-700 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Insurance Certificate:</strong> Public liability insurance is REQUIRED - your account will remain inactive until uploaded</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Portfolio Photos:</strong> At least one example of your work must be uploaded</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Email Verification:</strong> You must verify your email before completing registration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Insurance approval required to view the available jobs in your area</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>

            {tradesmanId && (
              <TradesmanPortfolioUpload
                tradesmanId={tradesmanId}
                onUploadComplete={() => {}}
                onValidationChange={setCanComplete}
              />
            )}

            <div className="space-y-4 mt-6">
              {/* Back to Previous Step */}
              <Button 
                onClick={() => setCurrentStep(2)} 
                variant="outline" 
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                Back to Profile Setup
              </Button>

              {/* Email Verification Card */}
              <Card className="p-4 bg-gradient-to-r from-blue-50 to-blue-100/50 border border-blue-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-blue-900">Email Verification (Required)</h3>
                    <p className="text-sm text-blue-700">Verify your email to complete your registration</p>
                  </div>
                  <Button 
                    onClick={handleShowEmailVerification}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    {emailSent ? "Enter Code" : "Verify Email"}
                  </Button>
                </div>
              </Card>

              {/* Complete Registration Button */}
              <Button 
                onClick={handleCompleteRegistration}
                disabled={!canComplete}
                className="w-full h-12 bg-gradient-hero text-white shadow-glow hover:shadow-elegant transition-all duration-300 border-0"
                size="lg"
              >
                Complete Registration
              </Button>

              {!canComplete && (
                <p className="text-center text-sm text-red-600 font-medium">
                  ⚠️ Please upload both insurance certificate and portfolio photos to complete registration
                </p>
              )}
            </div>
          </div>
        )}

        {/* Email Verification Dialog */}
        {showEmailVerification && userProfile && (
          <EmailVerificationDialog
            isOpen={showEmailVerification}
            onClose={() => setShowEmailVerification(false)}
            userEmail={userProfile.email}
            isVerified={userProfile.email_verified || false}
            onVerificationUpdate={() => {
              // Refresh user profile
              const refreshProfile = async () => {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                  const { data: profile } = await supabase
                    .from("profiles")
                    .select("*")
                    .eq("user_id", user.id)
                    .single();
                  if (profile) {
                    setUserProfile(profile);
                  }
                }
              };
              refreshProfile();
            }}
          />
        )}
      </div>
    </div>
  );
}

export default TradesmanRegistration;